import { Eye } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";

import { useGetCargoOrders, useGetTradeOrderStatuses } from "@/common/api";
import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
  TruncateTooltip,
  Card,
  CardContent,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { useUser } from "@/common/hooks";
import { getStatusData, formatDatetime } from "@/common/lib";

const LIMIT = 10;

export function OrdersPage() {
  const { user } = useUser();
  const [, setLocation] = useLocation();

  const [page, setPage] = useState(1);
  const [createdAt, setCreatedAt] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [statusId, setStatusId] = useState("");

  const { data: statusesData, isLoading: isStatusesLoading } = useGetTradeOrderStatuses();

  const {
    data: ordersData,
    isLoading: isOrdersLoading,
    error: ordersError,
  } = useGetCargoOrders(
    {
      userId: user?.id!,
      statusId: statusId === "" ? undefined : statusId,
      date: createdAt,
      orderNumber: orderNumber || undefined,
      limit: LIMIT,
      page,
    },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const totalPages = ordersData?.data.payload?.totalPage ?? null;
  const totalItems = ordersData?.data.payload?.totalItems ?? null;
  const items = ordersData?.data.payload?.items ?? null;
  const statuses = statusesData?.data.payload ?? null;

  const handleReset = useCallback(() => {
    setStatusId("");
    setCreatedAt("");
    setOrderNumber("");
  }, []);

  const handleRowClick = useCallback(
    (item: { id?: string; myParticipation?: boolean } | null) => {
      if (!item?.id) return;

      const participant = item.myParticipation ? 1 : 0;
      const basePath = ROUTES.ORDER_ID.replace(":id", item.id);

      // Добавляем в URL параметр participant=true/false в зависимости от myParticipation
      setLocation(`${basePath}?participant=${participant}`);
    },
    [setLocation],
  );

  const rows = useMemo(
    () => (
      <>
        {items?.map((item) => (
          <TableRow
            key={item.id}
            className="cursor-pointer hover:bg-gray-50"
            onClick={() => handleRowClick(item)}
          >
            <TableCell className="font-medium">{item.orderNumber}</TableCell>

            <TableCell>
              <p className="text-sm text-nowrap">c {formatDatetime(item.tradeBegin)}</p>
              <p className="text-sm text-nowrap">по {formatDatetime(item.tradeEnd)}</p>
            </TableCell>

            <TableCell>
              <Badge className={getStatusData(item.status).color}>
                {getStatusData(item.status).label}
              </Badge>
            </TableCell>

            <TableCell>
              <p className="text-sm text-nowrap">c {formatDatetime(item.stageBegin)}</p>
              <p className="text-sm text-nowrap">по {formatDatetime(item.endBegin)}</p>
            </TableCell>

            <TableCell>
              <TruncateTooltip text={item.loadingStation ?? ""} className="text-sm font-medium" />
              <p className="text-sm font-medium">({item.loadingRoad})</p>
            </TableCell>

            <TableCell>{item.participantsCount ?? 0}</TableCell>

            <TableCell>
              <TruncateTooltip text={item.cargoName ?? ""} className="text-sm font-medium" />
              <p className="text-sm">({item.cargoWeight} т.)</p>
            </TableCell>

            <TableCell>
              {item.wagonsType && item.totalWagons && (
                <>
                  <p className="text-sm font-medium">{item.wagonsType}</p>
                  <p className="text-sm">{`(${item.totalWagons} в.)`}</p>
                </>
              )}
            </TableCell>

            <TableCell>{item.myParticipation ? "Участвую" : "Не участвую"}</TableCell>

            <TableCell>
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowClick(item);
                }}
              >
                <Eye className="h-4 w-4" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </>
    ),
    [items, handleRowClick],
  );

  return (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold Main-orange">Личный кабинет собственника вагонов</h2>
        <p className="text-lg font-medium text-gray-700 mt-2">Все заявки</p>
      </div>
      <Card className="mb-6">
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-Text-main leading-4 mb-1">
                Статус
              </label>
              <Select
                value={statusId}
                onValueChange={setStatusId}
                disabled={isStatusesLoading}
                placeholder="Выберите статус"
              >
                <SelectContent>
                  <SelectItem value="">Все статусы</SelectItem>
                  {statuses?.map((status) => (
                    <SelectItem key={status.code} value={String(status.id)}>
                      {status.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-Text-main leading-4 mb-1">
                Дата создания
              </label>
              <Input
                key="createdAt"
                type="date"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                placeholder="ДД.ММ.ГГГГ"
                className="block"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-Text-main leading-4 mb-1">
                Номер заявки
              </label>
              <Input
                placeholder="Введите № заявки"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full" onClick={handleReset}>
                Сбросить
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Table */}
      <div className="mt-6">
        <Card>
          <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
            {isOrdersLoading ? (
              <div className="text-center py-8">
                <p className="caption">Загрузка...</p>
              </div>
            ) : items?.length === 0 || ordersError ? (
              <div className="text-center py-8">
                <p className="caption">Нет инициированных процедур</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center text-xs font-semibold leading-4 text-Text-secondary">
                      № заявки
                    </TableHead>
                    <TableHead className="text-center">Период проведения</TableHead>
                    <TableHead className="text-center">Статус</TableHead>
                    <TableHead className="text-center">Период этапа</TableHead>
                    <TableHead className="text-center">Станция отправления (дорога)</TableHead>
                    <TableHead className="text-center">Количество участников</TableHead>
                    <TableHead className="text-center">Груз</TableHead>
                    <TableHead className="text-center">Вагоны</TableHead>
                    <TableHead className="text-center">Мое участие</TableHead>
                    <TableHead className="text-center">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>{rows}</TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6">
        <TablePagination
          currentPage={page}
          totalPages={totalPages ?? 0}
          totalItems={totalItems ?? 0}
          pageSize={LIMIT}
          onPageChange={(page) => {
            setPage(page);
          }}
        />
      </div>
    </>
  );
}
